import React, { useState, useEffect, useContext } from 'react';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { GiHamburgerMenu } from "react-icons/gi";
import SettingsPanelActor from './SettingsPanelActor';
import SettingsPanelOperator from './SettingsPanelOperator';
import { Box, Typography, TextField, Select, MenuItem, Button, Checkbox, FormControlLabel } from "@mui/material";
import { AppContextisChecked } from '../context/AppContextisChecked';
import ChatHistoryList from './ChatHistoryList';
import { UserAuth } from '../context/AuthContext';

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, "Helvetica Neue", Arial, sans-serif';
const SIDEBAR_BG = '#f5f4ee';

export default function SlidingSidebar() {
  const [state, setState] = useState({ left: true });
  const [loop, setLoop] = useState(() => localStorage.getItem('loops') || 2);
  const { isChecked } = useContext(AppContextisChecked); // Detect isChecked from context
  const { user } = UserAuth() || {};
  const [isMobile, setIsMobile] = useState(false);
  const closeDrawer = () => setState({ left: false });
  
  // console.log('SlidingSlidebar *', isChecked);
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    if (!localStorage.getItem('loops')) {
      localStorage.setItem('loops', '1');
      setLoop(1);
    }
  }, []);

  const toggleDrawer = (anchor, open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) { return; }
    setState((prevState) => ({ ...prevState, [anchor]: open }));
  };

  const handleDoneClick = () => { setState({ left: false }); };
  // REMOVE "OpenAI" from these arrays:
  const api_provider0 = ["Deepseek", "Anthropic"]; // Remove "OpenAI"
  const api_provider1 = ["Anthropic", "Deepseek", ]; // Remove "OpenAI"
  const api_provider_operator = ["Google Gemini", "Deepseek", "Anthropic"]; // Remove "OpenAI"
  
  const list = (anchor) => (
    <Box sx={{
      width: anchor === 'top' || anchor === 'bottom' ? 'auto' : (isMobile ? 286 : 304),
      padding: isMobile ? '6px 6px 8px' : '8px 8px 10px',
      fontFamily: FONT,
      background: SIDEBAR_BG,
      minHeight: '100%'
    }}
    role="presentation"
    onKeyDown={toggleDrawer(anchor, true)}>

      {/* Harmonize the MUI settings form with the rest of the sidebar's typography. */}
      <style>{`
        .cs-settings, .cs-settings * { font-family: ${FONT} !important; }
        .cs-settings .MuiTypography-root { font-size: 12.5px !important; font-weight: 600 !important; color: #3d3d3a !important; letter-spacing: 0 !important; margin-bottom: 2px !important; }
        .cs-settings .MuiListItemButton-root { padding: 6px 2px !important; border-radius: 8px !important; }
        .cs-settings .MuiInputBase-root { background: #ffffff !important; color: #3d3d3a !important; border-radius: 8px !important; font-size: 13px !important; height: 32px !important; }
        .cs-settings .MuiSelect-select { color: #3d3d3a !important; padding-top: 6px !important; padding-bottom: 6px !important; }
        .cs-settings .MuiOutlinedInput-notchedOutline { border-color: #e0ded3 !important; }
        .cs-settings .MuiSvgIcon-root { color: #73736b !important; }
        .cs-settings .MuiSlider-root { color: #c2613f !important; }
        .cs-settings .MuiDivider-root { border-color: #e3e1d8 !important; background: transparent !important; }
      `}</style>

      {/* Brand + Done (top-right corner of the sidebar) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 6px 4px 10px' }}>
        <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: '15px', color: '#3d3d3a', letterSpacing: '-0.01em' }}>
          RAGRecruit
        </span>
        <Button
          variant="text"
          sx={{
            color: '#73736b',
            fontFamily: FONT,
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            padding: '4px 8px',
            minWidth: 'auto'
          }}
          onClick={handleDoneClick}>
          Done
        </Button>
      </div>

      {/* Chats — merged in from the former separate chat-history rail. */}
      {user && <ChatHistoryList onAfterAction={closeDrawer} />}
      {user && <Divider sx={{ margin: '12px 10px 2px', borderColor: '#e3e1d8' }} />}

      {!isChecked && <div className="cs-settings">
        <List sx={{ padding: isMobile ? '0 8px' : '0' }}>
          {['Actor Model'].map((text, idx) => (
            <ListItem key={text} disablePadding sx={{ marginBottom: isMobile ? '4px' : '0' }}>
              <ListItemButton sx={{ padding: isMobile ? '4px' : '8px' }}>
                {idx === 0 && <SettingsPanelActor name={text} idx={idx} api_provider={api_provider0} ai_agent={false} isMobile={isMobile} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </div>}
      {isChecked && <div className="cs-settings">
        <List sx={{ padding: isMobile ? '0 8px' : '0' }}>
          {['AI Agent Model'].map((text, idx) => (
            <ListItem key={text} disablePadding sx={{ marginBottom: isMobile ? '4px' : '0' }}>
              <ListItemButton sx={{ padding: isMobile ? '4px' : '8px' }}>
                {idx === 0 && <SettingsPanelOperator name={text} idx={idx} api_provider={api_provider_operator} ai_agent={true} isMobile={isMobile} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </div>}
      <div style={{ padding: '10px 10px 0' }}>
        <div style={{ fontFamily: FONT, fontSize: '11.5px', lineHeight: 1.5, color: '#9a9a90' }}>
          Questions or feedback? Open an issue at github.com/sanyathoque/RAGRecruitLegal
        </div>
        <div style={{ fontFamily: FONT, fontSize: '11.5px', color: '#b0b0a6', marginTop: 4 }}>
          v3.2.8
        </div>
      </div>

      {/* Account footer — merged in from the former chat-history rail. */}
      {user && (
        <>
          <Divider sx={{ margin: '12px 10px 0', borderColor: '#e3e1d8' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px 6px' }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#c2613f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, fontFamily: FONT }}>
              {(user.email || '?')[0].toUpperCase()}
            </div>
            <span style={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: '13px', color: '#73736b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={user.email}>
              {user.email}
            </span>
          </div>
        </>
      )}
    </Box>
  );

  return (
    <div>
      <Button onClick={toggleDrawer('left', true)}>
        <GiHamburgerMenu style={{ 
          height: isMobile ? "20px" : "2.5vh", 
          width: isMobile ? "20px" : "2.5vw", 
          color: "rgb(75, 85, 99)" 
        }} />
      </Button>
      <Drawer 
        anchor="left" 
        open={state.left} 
        onClose={toggleDrawer('left', false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '85%' : 'auto',
            maxWidth: isMobile ? '320px' : '340px',
            background: SIDEBAR_BG,
            borderRight: '1px solid #e6e4da'
          }
        }}
      >
        {list('left')}
      </Drawer>
    </div>
  );
}